import type { MenuContentProps } from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import React, { type SyntheticEvent, useCallback, useMemo } from 'react';
import { Box } from '../../box/Box';
import PopoverConfirm from '../../popover/PopoverConfirm';
import Switch from '../../Switch';
import { Text } from '../../text/Text';
import Combobox from '../combobox/Combobox';
import sharedStyles from '../radix-floating-elements.module.scss';
import { Slider } from '../slider/Slider';
import type {
  DropdownCheckboxItemOption,
  DropdownGroupOption,
  DropdownItemOption,
  DropdownLabelOption,
  DropdownMenuItem,
  DropdownMenuProps,
  DropdownRadioGroupOption,
  DropdownRadioItemOption,
  DropdownSliderOption,
  DropdownSubMenuComboboxOption,
  DropdownSubMenuOption,
  DropdownSwitchOption,
} from './DropdownMenu.types';
import { GenericItemBody } from './DropdownMenuItemBody';
import styles from './DropdownMenuPrimitive.module.scss';
import { DropdownMenu as DropdownMenuPrimitive } from './DropdownMenuPrimitives';

/**
 * PURPOSE:
 * A DX & user-friendly dropdown menu for use everywhere in the app that has
 * the full range of visual & functional expression we need.
 *
 * It builds strickly on the DropdownMenuPrimitive components,
 * to deliver a complete and accessible dropdown menu component for Timely
 *
 * @todo Look at existing "good" uses of various PopoverDropdownList & friends, only implement what we need of props/types.
 *
 * @link https://www.radix-ui.com/primitives/docs/components/dropdown-menu
 */
export const DropdownMenu = React.memo((props: DropdownMenuProps) => {
  const {
    trigger,
    closeOnSelect = false,
    onOpenChange,
    onAction,
    width,
    maxWidth,
    items,
    // MenuContentProps
    side,
    align,
    sticky,
    collisionPadding,
    collisionBoundary,
    avoidCollisions,
    sideOffset,
    alignOffset,
    hideWhenDetached,
    contentClassName,
    style,
    disabled = false,
    returnFocusOnClose = true,
    ...rest
  } = props;

  // Same workaround as applied in DialogComposition
  const handleEscapeKeyDown = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handlePreventDefault = useCallback((event: Event | SyntheticEvent) => {
    event.preventDefault();
  }, []);

  const computedStyle = useMemo(
    () => ({ ...style, width, maxWidth: maxWidth || width }),
    [maxWidth, style, width]
  );

  const contentProps: MenuContentProps = {
    side,
    align,
    sticky,
    collisionPadding,
    collisionBoundary,
    avoidCollisions,
    sideOffset,
    alignOffset,
    hideWhenDetached,
    onCloseAutoFocus: !returnFocusOnClose ? handlePreventDefault : undefined,
    className: contentClassName,
    style: computedStyle,
  };

  return (
    <DropdownMenuPrimitive.Root {...rest} onOpenChange={onOpenChange}>
      <DropdownMenuPrimitive.Trigger asChild disabled={disabled}>
        {trigger}
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content {...contentProps} onEscapeKeyDown={handleEscapeKeyDown}>
          <RenderMenuItems closeOnSelect={closeOnSelect} onAction={onAction} items={items} />
          {/* <DropdownMenuPrimitive.Arrow /> */}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
});
DropdownMenu.displayName = 'DropdownMenu';

export default DropdownMenu;

/**
 *
 * Individual Building Blocks
 */

type ItemProps = {
  item: DropdownItemOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};

const DropdownMenuItemComponent = ({ item, closeOnSelect, onAction, ...props }: ItemProps) => {
  // Use item-specific closeOnSelect if defined, otherwise use the global setting
  const itemCloseOnSelect = 'closeOnSelect' in item ? item.closeOnSelect : closeOnSelect;

  const handleSelect = useCallback(
    (event: Event | SyntheticEvent) => {
      if (item.disabled) {
        event.preventDefault();
        return;
      }
      if (!itemCloseOnSelect) event.preventDefault();
      item.onSelect?.(event, item);
      onAction?.({ event, item });
    },
    [item, onAction, itemCloseOnSelect]
  );

  if (item.confirm && !item.disabled) {
    return (
      <PopoverConfirm
        {...item.confirm}
        onConfirm={handleSelect}
        stopPropagation
        usePortal
        zIndex={1000}
        target={
          <DropdownMenuPrimitive.Item
            {...props}
            variant={item.variant}
            onSelect={e => e.preventDefault()}
          >
            <GenericItemBody item={item} />
          </DropdownMenuPrimitive.Item>
        }
      />
    );
  }

  return (
    <DropdownMenuPrimitive.Item {...props} variant={item.variant} onSelect={handleSelect}>
      <GenericItemBody item={item} />
    </DropdownMenuPrimitive.Item>
  );
};
DropdownMenuItemComponent.displayName = 'DropdownMenuItemComponent';

type SliderProps = {
  item: DropdownSliderOption;
  onAction: DropdownMenuProps['onAction'];
};
/** Todo: re-consider wrapper, it breaks keyboard navigation. Maybe `Item` /w onSelect preventDefault will work? */
const DropdownMenuSliderComponent = ({ item, onAction, ...props }: SliderProps) => {
  const handleValueCommit = useCallback(
    (value: number[]) => {
      item.slider.onValueCommit?.(value);
      onAction?.({ item, value });
    },
    [item, onAction]
  );

  return (
    <Box.Column
      {...props}
      className={classNames(
        sharedStyles.dropdownMenuItemBase,
        'variant' in item && item.variant && styles[`variant-${item.variant}`]
      )}
    >
      <Box.Row width="100%">
        <GenericItemBody item={{ ...item, description: undefined }} />
      </Box.Row>
      <Slider {...item.slider} onValueCommit={handleValueCommit} />
      {!!item.description && (
        <Text.Label ellipsis={2} size="xsmall" color="lowContrast">
          {item.description}
        </Text.Label>
      )}
    </Box.Column>
  );
};
DropdownMenuSliderComponent.displayName = 'DropdownMenuSliderComponent';

type SwitchProps = {
  item: DropdownSwitchOption;
  onAction: DropdownMenuProps['onAction'];
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
};

const DropdownMenuSwitchComponent = ({ item, onAction, closeOnSelect, ...props }: SwitchProps) => {
  // Use item-specific closeOnSelect if defined, otherwise use the global setting
  const itemCloseOnSelect = 'closeOnSelect' in item ? item.closeOnSelect : closeOnSelect;

  const handleSelect = useCallback(
    (event: Event) => {
      if (!itemCloseOnSelect) event.preventDefault();
      const checked = !item.checked;
      item.onCheckedChange?.(checked, item);
      onAction?.({ item, checked });
    },
    [item, onAction, itemCloseOnSelect]
  );

  return (
    <DropdownMenuPrimitive.Item {...props} variant={item.variant} onSelect={handleSelect}>
      <DropdownMenuPrimitive.Icon style={{ paddingTop: 1 }}>
        <Switch
          size="xsmall"
          name={item.key}
          disabled={item.disabled}
          checked={item.checked}
          // Don't use the actual Switch's onChange, but if defined, it'll be called in handler
          onChangeChecked={() => {}}
          readOnly
        />
      </DropdownMenuPrimitive.Icon>
      <GenericItemBody item={item} />
    </DropdownMenuPrimitive.Item>
  );
};
DropdownMenuSwitchComponent.displayName = 'DropdownMenuSwitchComponent';

export const DropdownMenuLabelComponent = ({ item }: { item: DropdownLabelOption }) => (
  <DropdownMenuPrimitive.Label
    className={classNames(sharedStyles.dropdownMenuLabel, {
      [sharedStyles.indent]: item.indent ?? true,
    })}
  >
    {item.label}
  </DropdownMenuPrimitive.Label>
);
DropdownMenuLabelComponent.displayName = 'DropdownMenuLabelComponent';

export const DropdownMenuSeparatorComponent = () => <DropdownMenuPrimitive.Separator />;

type CheckboxProps = {
  item: DropdownCheckboxItemOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};

const DropdownMenuCheckboxItemComponent = ({
  item,
  onAction,
  closeOnSelect,
  ...rest
}: CheckboxProps) => {
  // Use item-specific closeOnSelect if defined, otherwise use the global setting
  const itemCloseOnSelect = 'closeOnSelect' in item ? item.closeOnSelect : closeOnSelect;

  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      item.onCheckedChange?.(checked, item);
      onAction?.({ checked, item });
    },
    [item, onAction]
  );

  const handleSelect = useCallback(
    (event: Event) => {
      if (!itemCloseOnSelect) event.preventDefault();
    },
    [itemCloseOnSelect]
  );

  return (
    <DropdownMenuPrimitive.CheckboxItem
      checked={item.checked}
      {...rest}
      variant={item.variant}
      onSelect={handleSelect}
      onCheckedChange={handleCheckedChange}
    >
      <GenericItemBody item={item} />
    </DropdownMenuPrimitive.CheckboxItem>
  );
};
DropdownMenuCheckboxItemComponent.displayName = 'DropdownMenuCheckboxItemComponent';

type RadioItemProps = {
  item: DropdownRadioItemOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  disabled?: boolean;
};

const DropdownMenuRadioItemComponent = ({ item, closeOnSelect, ...props }: RadioItemProps) => {
  // Use item-specific closeOnSelect if defined, otherwise use the global setting
  const itemCloseOnSelect = 'closeOnSelect' in item ? item.closeOnSelect : closeOnSelect;

  const onSelect = useCallback(
    (event: Event) => !itemCloseOnSelect && event.preventDefault(),
    [itemCloseOnSelect]
  );
  return (
    <DropdownMenuPrimitive.RadioItem
      onSelect={onSelect}
      // @ts-expect-error Re-visit this if it's a real problem
      value={item.value}
      variant={item.variant}
      {...props}
    >
      <GenericItemBody item={item} />
    </DropdownMenuPrimitive.RadioItem>
  );
};
DropdownMenuRadioItemComponent.displayName = 'DropdownMenuRadioItemComponent';

type RadioGroupProps = {
  item: DropdownRadioGroupOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
  disabled?: boolean;
};

const DropdownMenuRadioGroupComponent = ({
  item,
  onAction,
  closeOnSelect,
  disabled = false,
  ...props
}: RadioGroupProps) => {
  const handleValueChange = useCallback(
    (value: string) => {
      const selectedItem = item.subOptions.find(subItem => subItem.value === value);
      item.onValueChange?.(value, selectedItem!);
      onAction?.({ item: selectedItem! });
    },
    [item, onAction]
  );

  return (
    <DropdownMenuPrimitive.RadioGroup
      {...props}
      // @ts-expect-error Re-visit this if it's a real problem
      value={item.currentValue}
      onValueChange={handleValueChange}
    >
      <Box.Row alignContent="center">
        <DropdownMenuPrimitive.Label>{item.label}</DropdownMenuPrimitive.Label>
      </Box.Row>
      {item.subOptions.map((subItem, index) => (
        <DropdownMenuRadioItemComponent
          closeOnSelect={closeOnSelect}
          key={`${index}`}
          item={subItem}
          disabled={disabled}
        />
      ))}
    </DropdownMenuPrimitive.RadioGroup>
  );
};
DropdownMenuRadioGroupComponent.displayName = 'DropdownMenuRadioGroupComponent';

type GroupProps = {
  item: DropdownGroupOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};

const DropdownMenuGroupComponent = ({ item, closeOnSelect, onAction, ...props }: GroupProps) => {
  return (
    <DropdownMenuPrimitive.Group {...props}>
      <DropdownMenuPrimitive.Label>{item.label}</DropdownMenuPrimitive.Label>
      {item.subOptions.map((subItem, index) => (
        <RenderMenuItem
          key={index}
          closeOnSelect={closeOnSelect}
          onAction={onAction}
          item={subItem}
        />
      ))}
    </DropdownMenuPrimitive.Group>
  );
};
DropdownMenuGroupComponent.displayName = 'DropdownMenuGroupComponent';

type SubMenuProps = {
  item: DropdownSubMenuOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};

const DropdownMenuSubMenuComponent = ({
  item,
  onAction,
  closeOnSelect,
  ...props
}: SubMenuProps) => {
  // Use item-specific closeOnSelect if defined, otherwise use the global setting
  const itemCloseOnSelect = 'closeOnSelect' in item ? item.closeOnSelect : closeOnSelect;

  const handleSubTriggerSelect = useCallback(
    (event: Event | SyntheticEvent) => {
      if (!itemCloseOnSelect) event.preventDefault();
    },
    [itemCloseOnSelect]
  );

  return (
    <DropdownMenuPrimitive.Sub {...props}>
      <DropdownMenuPrimitive.SubTrigger
        disabled={item.disabled}
        variant={item.variant}
        onClick={item.onClick}
        onSelect={handleSubTriggerSelect}
      >
        <GenericItemBody item={item} />
      </DropdownMenuPrimitive.SubTrigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.SubContent style={{ width: item.width, maxWidth: item.width }}>
          {item.subOptions.map((subItem, index) => (
            <RenderMenuItem
              key={index}
              closeOnSelect={itemCloseOnSelect}
              onAction={onAction}
              item={subItem}
            />
          ))}
        </DropdownMenuPrimitive.SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  );
};
DropdownMenuSubMenuComponent.displayName = 'DropdownMenuSubMenuComponent';

type DropdownMenuComboboxProps = {
  item: DropdownSubMenuComboboxOption;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};
const DropdownMenuComboboxComponent = ({
  item,
  onAction,
  closeOnSelect,
  ...props
}: DropdownMenuComboboxProps) => {
  return (
    <DropdownMenuPrimitive.Sub {...props}>
      <DropdownMenuPrimitive.SubTrigger variant={item.variant}>
        <GenericItemBody item={item} />
        <DropdownMenuPrimitive.RightSlot />
      </DropdownMenuPrimitive.SubTrigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.SubContent asChild>
          <Combobox items={item.subOptions} onSelect={onAction} />
        </DropdownMenuPrimitive.SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  );
};
DropdownMenuComboboxComponent.displayName = 'DropdownMenuComboboxComponent';

type RenderItemProps = {
  item: DropdownMenuItem;
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};
const RenderMenuItem = ({ item, onAction, ...rest }: RenderItemProps) => {
  const readonly = 'readonly' in item && !!item.readonly ? true : undefined;
  const disabled = readonly || ('disabled' in item && !!item.disabled) ? true : undefined;
  // @ts-expect-error (since it just adds noise to properly type-check this)
  const testid = item['data-testid'] || item.key || item.label || undefined;
  const extra = {
    disabled,
    'data-readonly': readonly,
    'data-testid': testid,
    'data-indent': 'indent' in item && item.indent,
  };

  switch (item.type) {
    case 'group':
      return <DropdownMenuGroupComponent onAction={onAction} {...rest} {...extra} item={item} />;
    case 'item':
      return <DropdownMenuItemComponent onAction={onAction} {...rest} {...extra} item={item} />;
    case 'label':
      return <DropdownMenuLabelComponent {...rest} {...extra} item={item} />;
    case 'separator':
      return <DropdownMenuSeparatorComponent />;
    case 'checkboxItem':
      return (
        <DropdownMenuCheckboxItemComponent onAction={onAction} {...rest} {...extra} item={item} />
      );
    case 'radioItem':
      return <DropdownMenuRadioItemComponent {...rest} item={item} {...extra} />;
    case 'radioGroup':
      return (
        <DropdownMenuRadioGroupComponent onAction={onAction} {...rest} item={item} {...extra} />
      );
    case 'subMenu':
      return <DropdownMenuSubMenuComponent onAction={onAction} {...rest} item={item} {...extra} />;
    case 'slider': {
      const { closeOnSelect, ...restSlider } = rest;
      return (
        <DropdownMenuSliderComponent onAction={onAction} {...restSlider} item={item} {...extra} />
      );
    }
    case 'switch':
      return <DropdownMenuSwitchComponent onAction={onAction} {...rest} {...extra} item={item} />;
    case 'combobox':
      return <DropdownMenuComboboxComponent onAction={onAction} {...rest} {...extra} item={item} />;
    case 'custom':
      return (
        <DropdownMenuPrimitive.CustomItem {...extra}>
          {item.content}
        </DropdownMenuPrimitive.CustomItem>
      );
    case 'message':
      return <DropdownMenuPrimitive.Message {...extra} item={item} />;
    default:
      return <div>RenderMenuItem doesnt support this item</div>;
  }
};

type RenderOptionsProps = {
  items: DropdownMenuItem[];
  closeOnSelect: DropdownMenuProps['closeOnSelect'];
  onAction: DropdownMenuProps['onAction'];
};

const RenderMenuItems = ({ items, ...rest }: RenderOptionsProps) => {
  return (
    <>
      {items.map((item, index) => (
        <RenderMenuItem key={`${index}-${item.type}`} {...rest} item={item} />
      ))}
    </>
  );
};
RenderMenuItem.displayName = 'RenderMenuItem';
